import gql from 'graphql-tag';
import { provideApolloClient } from '@vue/apollo-composable';
import { reactive, ref } from 'vue';
import useDecryptSymbols from './useDecryptSymbols';
import apolloClient from './useApolloClient';
import useStatesAndCountries from './useStatesAndCountries';
import useAlerts from './useAlerts';
import useUsersQuery from './useUsersQuery';
const { addAlert } = useAlerts();
const { decryptSymbols, encryptSymbols } = useDecryptSymbols();
const { stateAbbreviations } = useStatesAndCountries();
const { allUsers } = useUsersQuery();

const reportData = ref([]);
const loading = ref(false);

const stateList = stateAbbreviations.value;

const columnsList = [
  { name: 'NAME', title: 'Company Name', flexWidth: 3, order: 1 },
  { name: 'COMPANYPHONE', title: 'Company Phone', flexWidth: 3, order: 2 },
  { name: 'CATEGORY', title: 'Category', flexWidth: 3, order: 3 },
  { name: 'INSIDESALESPERSON', title: 'Inside Salesperson', flexWidth: 3, order: 4 },
  { name: 'OUTSIDESALESPERSON', title: 'Outside Salesperson', flexWidth: 3, order: 5 },
  { name: 'CUSTOMERDETAILS', title: 'Customer Details', flexWidth: 3, order: 6 },
  { name: 'VENDORDETAILS', title: 'Vendor Details', flexWidth: 3, order: 7 },
  { name: 'VENDORDEPARTMENTS', title: 'Vendor Departments', flexWidth: 3, order: 8 },
  { name: 'ARCHITECTDETAILS', title: 'Architect/Engineer Details', flexWidth: 3, order: 9 },
  { name: 'WEBADDRESS', title: 'Website', flexWidth: 3, order: 10 },
  { name: 'LINE1', title: 'Address', flexWidth: 3, order: 11 },
  { name: 'CITY', title: 'City', flexWidth: 2, order: 12 },
  { name: 'STATE', title: 'State', flexWidth: 2, order: 13 },
  { name: 'ZIP', title: 'Zip', flexWidth: 2, order: 13 },
];

const filters = reactive({
  columns: ['NAME', 'COMPANYPHONE', 'WEBADDRESS', 'LINE1', 'CITY', 'STATE'],
  name: '',
  categories: [],
  insideSalespeople: [],
  outsideSalespeople: [],
  customerDetails: [],
  vendorDetails: [],
  vendorDepartments: [],
  architectDetails: [],
  states: [],
  city: '',
  zip: '',
});

const defaultFilters = JSON.parse(JSON.stringify(filters));

const resetFilters = () => {
  for (const key in filters) {
    filters[key] = defaultFilters[key];
  }
};

const GET_COMPANY_REPORT = gql`
  query Query($filters: CompanyReportFilters) {
    companyReport(filters: $filters) {
      companies {
        COMPANYID
        NAME
        OUTSIDESALES
        INSIDESALES
        COMPANYPHONE
        LINE1
        STATE
        POSTALCODE
        CITY
        CUSTOMERDETAILS
        VENDORDETAILS
        VENDORDEPT
        WEBADDRESS
        CATEGORY
        ARCHITECTDETAILS
      }
    }
  }
`;

const validateFilters = () => {
  if (!filters.columns.length) {
    addAlert('Please select at least one column', 'high', 5);
    return false;
  }
  if (
    !filters.name &&
    !filters.categories.length &&
    !filters.insideSalespeople.length &&
    !filters.outsideSalespeople.length &&
    !filters.customerDetails.length &&
    !filters.vendorDetails.length &&
    !filters.vendorDepartments.length &&
    !filters.architectDetails.length &&
    !filters.states.length &&
    !filters.city &&
    !filters.zip
  ) {
    addAlert('Please enter at least one filter', 'high', 5);
    return false;
  }
  return true;
};

const useCompanyReportQuery = function () {
  provideApolloClient(apolloClient);

  const runReport = async () => {
    if (!validateFilters()) return;

    loading.value = true;
    try {
      const filterParams = {
        name: filters.name,
        categories: encryptSymbols(filters.categories),
        insideSalespeople: filters.insideSalespeople,
        outsideSalespeople: filters.outsideSalespeople,
        customerDetails: encryptSymbols(filters.customerDetails),
        vendorDetails: encryptSymbols(filters.vendorDetails),
        vendorDepartments: encryptSymbols(filters.vendorDepartments),
        architectDetails: encryptSymbols(filters.architectDetails),
        states: filters.states.length === 0 ? [] : encryptSymbols(filters.states),
        city: filters.city,
        zip: filters.zip,
      };

      const { data } = await apolloClient.query({
        query: GET_COMPANY_REPORT,
        variables: { filters: filterParams },
        fetchPolicy: 'network-only',
      });

      if (data?.companyReport) {
        const decrypted = decryptSymbols(data.companyReport);
        formatReportData(decrypted);
      }
    } catch (err) {
      console.error('Error fetching customer report:', err);
      addAlert('Error fetching customer report', 'high', 4);
    } finally {
      loading.value = false;
    }
  };

  return {
    reportData,
    loading,
    filters,
    runReport,
    columnsList,
    stateList,
    validateFilters,
    resetFilters,
  };
};

function formatReportData(data) {
  // Helper function to get display name by user ID
  const getDisplayNameByUserId = (userId) => {
    if (!userId) return '';
    const user = allUsers?.find((user) => user.USERID === userId);
    return user ? user.DISPLAYNAME : '';
  };

  const formattedData = data.companies.map((company) => ({
    COMPANYID: company.COMPANYID || '',
    NAME: company.NAME || '',
    COMPANYPHONE: company.COMPANYPHONE || '',
    CATEGORY: company.CATEGORY || '',
    INSIDESALESPERSON: getDisplayNameByUserId(company.INSIDESALES),
    OUTSIDESALESPERSON: getDisplayNameByUserId(company.OUTSIDESALES),
    CUSTOMERDETAILS: Array.isArray(company.CUSTOMERDETAILS) ? company.CUSTOMERDETAILS.join(', ') : company.CUSTOMERDETAILS || '',
    VENDORDETAILS: Array.isArray(company.VENDORDETAILS) ? company.VENDORDETAILS.join(', ') : company.VENDORDETAILS || '',
    VENDORDEPARTMENTS: Array.isArray(company.VENDORDEPT) ? company.VENDORDEPT.join(', ') : company.VENDORDEPT || '',
    ARCHITECTDETAILS: Array.isArray(company.ARCHITECTDETAILS) ? company.ARCHITECTDETAILS.join(', ') : company.ARCHITECTDETAILS || '',
    WEBADDRESS: company.WEBADDRESS || '',
    LINE1: company.LINE1 || '',
    CITY: company.CITY || '',
    STATE: company.STATE || '',
    ZIP: company.POSTALCODE || '',
  }));

  reportData.value = formattedData;
}

export default useCompanyReportQuery;
